import React, { useState } from 'react';
import {
  Avatar,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { UsageData } from '../../hooks/useUsage';
import { getNavUsage, useAppNavActions } from '../../hooks/useAppNavActions';
import { useScribeNav } from '../../contexts/ScribeNavContext';
import { RECORD_TAB_ID, RecordingDot } from '../../constants/scribeTabs';
import AppNavRail from './AppNavRail';
import NotificationDropdown from './NotificationDropdown';

export const PHONE_BOTTOM_NAV_HEIGHT_PX = 56;
/** Bar height plus the device's bottom safe area (home indicator); 0 where there is none. */
export const PHONE_BOTTOM_NAV_CLEARANCE = `calc(${PHONE_BOTTOM_NAV_HEIGHT_PX}px + env(safe-area-inset-bottom, 0px))`;

interface AppNavProps {
  userName: string;
  onSignOut: () => void;
  usage: UsageData | null;
  usageLoading: boolean;
  /** False when the user cannot reach Scribe (trial expired): no tool items, since they would bounce back. */
  showTools?: boolean;
}

/**
 * Phones (below sm): a slim header with the account avatar menu, and a fixed bottom bar with
 * the Scribe tools. The bottom bar's height is cleared by Layout's main padding.
 */
const PhoneNav: React.FC<AppNavProps> = ({ userName, onSignOut, usage, usageLoading, showTools = true }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { visibleTabs, isRecording } = useScribeNav();
  const { activeTool, selectTool, goTo, signOut } = useAppNavActions(onSignOut);

  const { display: usageDisplay, exceeded } = getNavUsage(usage, usageLoading);
  const close = () => setAnchorEl(null);

  return (
    <>
      <Box
        component="header"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          height: 48,
          px: 1.5,
          flexShrink: 0,
          boxSizing: 'border-box',
          backgroundColor: 'var(--bg2)',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box component="img" src="/brand/app-icon.svg" alt="AI-Scribe" sx={{ height: 28, width: 'auto', borderRadius: '4px' }} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <NotificationDropdown color="var(--t2)" />
          <IconButton
            size="small"
            aria-label={`Account menu for ${userName}`}
            aria-controls={anchorEl ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={anchorEl ? 'true' : undefined}
            onClick={(e) => setAnchorEl(e.currentTarget)}
          >
            <Avatar sx={{ width: 30, height: 30, fontSize: '0.85rem', bgcolor: 'var(--s2)', color: 'var(--teal)' }}>
              {userName.trim().charAt(0).toUpperCase() || '?'}
            </Avatar>
          </IconButton>
        </Box>
        <Menu id="account-menu" anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={close}>
          <MenuItem disabled sx={{ opacity: '1 !important', fontSize: '0.85rem' }}>
            {userName}
          </MenuItem>
          {usageDisplay && (
            <MenuItem
              onClick={() => {
                close();
                void goTo('/subscription');
              }}
            >
              {usageDisplay.menuLabel}
            </MenuItem>
          )}
          {exceeded && (
            <MenuItem
              onClick={() => {
                close();
                void goTo('/pricing');
              }}
            >
              Upgrade
            </MenuItem>
          )}
          <Divider />
          <MenuItem
            onClick={() => {
              close();
              void goTo('/subscription');
            }}
          >
            Subscription
          </MenuItem>
          <MenuItem
            onClick={() => {
              close();
              void signOut();
            }}
          >
            Sign out
          </MenuItem>
        </Menu>
      </Box>

      {showTools && (
        <BottomNavigation
          component="nav"
          aria-label="Main navigation"
          showLabels
          value={activeTool ?? false}
          onChange={(_, id) => void selectTool(id)}
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1100,
            boxSizing: 'border-box',
            height: PHONE_BOTTOM_NAV_CLEARANCE,
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            backgroundColor: 'var(--bg2)',
            borderTop: '1px solid var(--border)',
            '& .MuiBottomNavigationAction-root': { minWidth: 0, color: 'var(--t2)' },
            '& .MuiBottomNavigationAction-root.Mui-focusVisible': { outline: '2px solid var(--teal)', outlineOffset: -2 },
            '& .Mui-selected': { color: 'var(--teal)' },
          }}
        >
          {visibleTabs.map((t) => (
            <BottomNavigationAction
              key={t.id}
              value={t.id}
              aria-current={activeTool === t.id ? 'page' : undefined}
              label={
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                  {t.shortLabel}
                  {t.id === RECORD_TAB_ID && isRecording && <RecordingDot />}
                </Box>
              }
              icon={<t.Icon size={22} strokeWidth={2} aria-hidden />}
            />
          ))}
        </BottomNavigation>
      )}
    </>
  );
};

/** Chooses the navigation for the screen: the left rail from sm up, the bottom bar below it. */
const AppNav: React.FC<AppNavProps> = (props) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  return isPhone ? <PhoneNav {...props} /> : <AppNavRail {...props} />;
};

export default AppNav;
